import { useRef, useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Check, Layers, MessageSquare, ShieldCheck, Zap } from 'lucide-react';
import { Button } from './Button';
import { executeRecaptchaV3 } from '../utils/recaptchaV3';

const COMMON_PROVIDERS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'live.com',
];

export const ContactSection = ({ id }: { id?: string }) => {
  const honeypotRef = useRef<HTMLInputElement | null>(null);
  const [formData, setFormData] = useState({
    inquiryType: 'Lifecycle Orchestration Quote',
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters.';
    }

    if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    } else {
      const domain = formData.email.split('@')[1]?.toLowerCase();
      if (COMMON_PROVIDERS.includes(domain)) {
        newErrors.email = 'Please use a professional/company email address.';
      }
    }

    if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) return;

    setStatus('loading');
    setSubmitMessage('');

    try {
      const recaptchaAction = 'lead_contact';
      const recaptchaToken = await executeRecaptchaV3(recaptchaAction);
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          website: honeypotRef.current?.value || '',
          recaptchaToken,
          recaptchaAction,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        const result = await response.json();
        if (response.ok && result.success) {
          setStatus('success');
          setSubmitMessage(result.message || 'Your request was submitted successfully.');
          setFormData({
            inquiryType: 'Lifecycle Orchestration Quote',
            firstName: '',
            lastName: '',
            email: '',
            message: '',
          });
          setErrors({});
          setTimeout(() => setStatus('idle'), 5000);
        } else {
          setSubmitMessage(result.error || 'Unable to submit the form right now.');
          setStatus('error');
        }
      } else {
        setSubmitMessage('The server returned an unexpected response. Please try again.');
        setStatus('error');
      }
    } catch (error) {
      console.error('Lead submission failed:', error);
      setSubmitMessage(error instanceof Error ? error.message : 'Unable to reach the lead API. Please make sure the app server is running.');
      setStatus('error');
    }
  };

  const responsePath = [
    {
      title: 'Discovery',
      desc: 'We identify the revenue, billing, marketplace, or lifecycle workflow you want to improve first.',
      icon: MessageSquare,
    },
    {
      title: 'Solution Map',
      desc: 'Our team maps your current systems, handoffs, integrations, and operational bottlenecks.',
      icon: Layers,
    },
    {
      title: 'Demo Plan',
      desc: 'You get a focused walkthrough around the flows that matter to your business.',
      icon: ShieldCheck,
    },
  ];

  return (
    <section id={id} className="relative overflow-hidden bg-white py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid items-stretch gap-6 lg:grid-cols-2">
          <div className="relative flex min-h-[620px] overflow-hidden rounded-[2rem] bg-slate-950 p-7 text-white shadow-[0_26px_90px_rgba(15,23,42,0.18)] md:p-8">
            <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#ff5d22]/20 blur-[90px]" />
            <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:42px_42px]" />
            <div className="relative z-10 flex w-full flex-col">
              <div>
                <div className="inline-flex rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.24em] text-[#ff8a5c]">
                  Connect
                </div>
                <h2 className="mt-5 max-w-xl text-3xl font-black leading-[0.98] tracking-tight text-white md:text-5xl">
                  Let's map your revenue operations.
                </h2>
                <p className="mt-5 max-w-xl text-sm font-bold leading-relaxed text-white/65 md:text-base">
                  Share what you are building, replacing, or scaling. We will route your request to the right AppGallop specialist.
                </p>
              </div>

              <div className="mt-8 border-y border-white/10">
                <div className="flex items-center justify-between gap-4 py-4">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.24em] text-[#ff8a5c]">Response Path</p>
                    <h3 className="mt-1 text-lg font-black tracking-tight text-white">What happens after you submit</h3>
                  </div>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#ff5d22] text-white shadow-lg shadow-[#ff5d22]/20">
                    <Zap size={18} />
                  </div>
                </div>

                {responsePath.map((item, index) => (
                  <div key={item.title} className="grid grid-cols-[44px_1fr] gap-4 border-t border-white/10 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#ff5d22]/20 bg-[#ff5d22]/10 text-[#ff8a5c]">
                      <item.icon size={16} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[8px] font-black uppercase tracking-widest text-white/35">0{index + 1}</span>
                        <h4 className="text-sm font-black text-white">{item.title}</h4>
                      </div>
                      <p className="mt-1 text-[11px] font-semibold leading-relaxed text-white/55">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative flex min-h-[620px] overflow-hidden rounded-[2rem] border border-black/15 bg-slate-50 p-7 shadow-[0_26px_90px_rgba(15,23,42,0.08)] md:p-8"
          >
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[#ff5d22]/8 blur-[70px]" />

            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative z-10 flex w-full flex-col items-center justify-center text-center"
              >
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-500">
                  <Check size={40} />
                </div>
                <h3 className="mb-2 text-2xl font-black">Request Received.</h3>
                <p className="max-w-sm font-bold text-black/50">
                  Our sales team has been notified and will reach out to you within 24 hours.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="relative z-10 flex w-full flex-col">
                <input
                  ref={honeypotRef}
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
                />
                <div className="mb-6">
                  <p className="text-[9px] font-black uppercase tracking-[0.24em] text-[#ff5d22]">Start the Conversation</p>
                  <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Tell us what you need.</h3>
                  <p className="mt-2 text-xs font-bold leading-relaxed text-black/45">
                    A short note is enough. Our team will follow up with the right next step.
                  </p>
                </div>

                <div className="space-y-3.5">
                  <div className="space-y-1">
                    <label className="ml-2 text-[9px] font-black uppercase tracking-widest text-black/30">Inquiry Type</label>
                    <select
                      value={formData.inquiryType}
                      onChange={(event) => setFormData({ ...formData, inquiryType: event.target.value })}
                      className="w-full rounded-lg border border-black/5 bg-white px-4 py-3 text-[11px] font-bold outline-none transition-all ring-[#ff5d22]/10 ring-offset-2 focus:border-[#ff5d22]/50 focus:ring-2"
                    >
                      <option>Lifecycle Orchestration Quote</option>
                      <option>Partner Integration</option>
                      <option>Global Expansion Support</option>
                      <option>Press & Media</option>
                    </select>
                  </div>

                  <div className="grid gap-3.5 md:grid-cols-2">
                    <div className="space-y-1">
                      <label className="ml-2 text-[9px] font-black uppercase tracking-widest text-black/30">First Name</label>
                      <input
                        type="text"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={(event) => setFormData({ ...formData, firstName: event.target.value })}
                        className={`w-full rounded-lg border bg-white px-4 py-3 text-[11px] font-bold outline-none transition-all ring-[#ff5d22]/10 ring-offset-2 focus:border-[#ff5d22]/50 focus:ring-2 ${errors.firstName ? 'border-red-500' : 'border-black/5'}`}
                      />
                      {errors.firstName && <p className="ml-2 text-[8px] font-bold text-red-500">{errors.firstName}</p>}
                    </div>
                    <div className="space-y-1">
                      <label className="ml-2 text-[9px] font-black uppercase tracking-widest text-black/30">Last Name</label>
                      <input
                        type="text"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={(event) => setFormData({ ...formData, lastName: event.target.value })}
                        className={`w-full rounded-lg border bg-white px-4 py-3 text-[11px] font-bold outline-none transition-all ring-[#ff5d22]/10 ring-offset-2 focus:border-[#ff5d22]/50 focus:ring-2 ${errors.lastName ? 'border-red-500' : 'border-black/5'}`}
                      />
                      {errors.lastName && <p className="ml-2 text-[8px] font-bold text-red-500">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="ml-2 text-[9px] font-black uppercase tracking-widest text-black/30">Professional Email</label>
                    <input
                      type="email"
                      placeholder="name@company.com"
                      value={formData.email}
                      onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                      className={`w-full rounded-lg border bg-white px-4 py-3 text-[11px] font-bold outline-none transition-all ring-[#ff5d22]/10 ring-offset-2 focus:border-[#ff5d22]/50 focus:ring-2 ${errors.email ? 'border-red-500' : 'border-black/5'}`}
                    />
                    {errors.email && <p className="ml-2 text-[8px] font-bold text-red-500">{errors.email}</p>}
                  </div>

                  <div className="space-y-1">
                    <label className="ml-2 text-[9px] font-black uppercase tracking-widest text-black/30">Message</label>
                    <textarea
                      rows={2}
                      placeholder="Briefly describe your requirements..."
                      value={formData.message}
                      onChange={(event) => setFormData({ ...formData, message: event.target.value })}
                      className={`w-full resize-none rounded-lg border bg-white px-4 py-3 text-[11px] font-bold outline-none transition-all ring-[#ff5d22]/10 ring-offset-2 focus:border-[#ff5d22]/50 focus:ring-2 ${errors.message ? 'border-red-500' : 'border-black/5'}`}
                    />
                    {errors.message && <p className="ml-2 text-[8px] font-bold text-red-500">{errors.message}</p>}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  variant="primary"
                  className={`mt-auto w-full !py-3.5 text-[9px] font-black uppercase tracking-widest shadow-xl shadow-[#ff5d22]/10 ${status === 'loading' ? 'cursor-not-allowed opacity-70' : ''}`}
                >
                  {status === 'loading' ? 'Processing...' : status === 'error' ? 'Error. Try Again' : 'Submit Request'}
                </Button>

                {status === 'error' && submitMessage && (
                  <p className="text-center text-[10px] font-bold leading-relaxed text-red-500">
                    {submitMessage}
                  </p>
                )}

                <a
                  href="mailto:sales@appgallop.com"
                  className="mt-3 flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white px-4 py-3 text-slate-950 transition-all hover:border-[#ff5d22]/40 hover:text-[#ff5d22]"
                >
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-widest text-black/35">Direct Email</p>
                    <p className="text-sm font-black">sales@appgallop.com</p>
                  </div>
                  <ArrowRight size={16} />
                </a>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
